import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { UserIcon, BellIcon, SettingsIcon, SaveIcon, Loader2Icon, CheckCircleIcon } from 'lucide-react';
import { AccountSettings } from '../components/AccountSettings';
import { StudyPreferences } from '../components/StudyPreferences';
import { NotificationSettings } from '../components/NotificationSettings';
import { Sitemap } from '../components/Sitemap';
type SettingsTab = 'account' | 'preferences' | 'notifications';
export function ProfileSettings() {
  const {
    isArabic
  } = useLanguage();
  const [activeTab, setActiveTab] = useState<SettingsTab>('account');
  const [isSaving, setIsSaving] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const handleSave = async () => {
    setIsSaving(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsSaving(false);
    setShowSaveSuccess(true);
    setTimeout(() => setShowSaveSuccess(false), 3000);
  };
  const tabs = [{
    id: 'account' as SettingsTab,
    label: {
      en: 'Account Settings',
      ar: 'إعدادات الحساب'
    },
    icon: UserIcon
  }, {
    id: 'preferences' as SettingsTab,
    label: {
      en: 'Study Preferences',
      ar: 'تفضيلات الدراسة'
    },
    icon: SettingsIcon
  }, {
    id: 'notifications' as SettingsTab,
    label: {
      en: 'Notifications',
      ar: 'الإشعارات'
    },
    icon: BellIcon
  }];
  return <div className="w-full px-6 md:px-10 lg:px-20 py-16" data-id="element-4130">
      <Sitemap currentPage={{
      en: 'Profile Settings',
      ar: 'إعدادات الملف الشخصي'
    }} breadcrumbs={[{
      label: {
        en: 'Profile',
        ar: 'الملف الشخصي'
      },
      href: '/profile'
    }]} data-id="element-4131" />
      <div className="max-w-4xl mx-auto" data-id="element-4132">
        {/* Header */}
        <div className="text-center mb-12" data-id="element-4133">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4" data-id="element-4134">
            {isArabic ? 'إعدادات الملف الشخصي' : 'Profile Settings'}
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300" data-id="element-4135">
            {isArabic ? 'قم بتخصيص تجربة التعلم الخاصة بك' : 'Customize your learning experience'}
          </p>
        </div>
        {/* Tabs */}
        <div className="flex flex-col md:flex-row gap-8 mb-8" data-id="element-4136">
          <div className="md:w-64 space-y-2" data-id="element-4137">
            {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`
                  w-full flex items-center gap-3 p-3 rounded-lg transition-colors
                  ${activeTab === tab.id ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300'}
                `} data-id="element-4138">
                <tab.icon className="w-5 h-5" data-id="element-4139" />
                <span data-id="element-4140">{isArabic ? tab.label.ar : tab.label.en}</span>
              </button>)}
          </div>
          {/* Content */}
          <div className="flex-1" data-id="element-4141">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6" data-id="element-4142">
              {activeTab === 'account' && <AccountSettings data-id="element-4143" />}
              {activeTab === 'preferences' && <StudyPreferences data-id="element-4144" />}
              {activeTab === 'notifications' && <NotificationSettings data-id="element-4145" />}
              {/* Save Button */}
              <div className="mt-8 flex items-center gap-4" data-id="element-4146">
                <button onClick={handleSave} disabled={isSaving} className="flex items-center justify-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50" data-id="element-4147">
                  {isSaving ? <Loader2Icon className="w-5 h-5 animate-spin" data-id="element-4148" /> : <SaveIcon className="w-5 h-5" data-id="element-4149" />}
                  <span data-id="element-4150">{isArabic ? 'حفظ الت��ييرات' : 'Save Changes'}</span>
                </button>
                {showSaveSuccess && <div className="flex items-center gap-2 text-green-600 dark:text-green-400" data-id="element-4151">
                    <CheckCircleIcon className="w-5 h-5" data-id="element-4152" />
                    <span data-id="element-4153">
                      {isArabic ? 'تم الحفظ بنجاح' : 'Changes saved successfully'}
                    </span>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}